/**
 * LoomCanvas Renderer
 * 
 * The heart of the "emotion naming loom" metaphor.
 * Draws a handwoven-looking textile from an ordered list of status threat threads.
 * 
 * Design goals:
 * - Feels crafted, not generated
 * - Color + density directly encodes the four load dimensions
 * - Every new thread visibly "thrown" then beaten into the cloth
 * - Exportable at high fidelity for public artifacts (R2)
 * - No individual thread is labeled on the public artifact view
 */

import type { Thread, LoomRenderOptions } from './types'

interface SeededRandom {
  next: () => number
}

function createSeededRandom(seed: number): SeededRandom {
  let s = seed
  return {
    next() {
      s = (s * 16807) % 2147483647
      return (s - 1) / 2147483646
    }
  }
}

function getThreadColor(t: Thread): string {
  // Map the four axes to a natural dyed thread color
  // High fragmentation + low clarity → cooler, dustier indigo
  // High recovery + decent clarity → warmer clay / ochre
  // High readiness → slightly brighter / lifted
  const delta = (t.clarity - t.frag) * 0.5
  const hue = 225 + delta * 7.5 // ~210-245 range (blue-indigo to muted teal)
  const sat = Math.max(28, Math.min(68, 38 + t.recovery * 3.2))
  const light = Math.max(34, Math.min(62, 46 + (t.readiness - 5) * 1.8))
  return `hsl(${hue.toFixed(1)} ${sat.toFixed(1)}% ${light.toFixed(1)}%)`
}

function getWeftTension(t: Thread): number {
  // High frag threads look more "tense" / slightly thinner + more jitter
  return 0.7 + (t.frag / 10) * 0.55
}

export function drawLoom(
  ctx: CanvasRenderingContext2D,
  opts: LoomRenderOptions
) {
  const { width, height, threads, animateNew = false, newThreadId, highQuality = false } = opts

  const dpr = highQuality ? 2 : 1
  const W = width * dpr
  const H = height * dpr

  ctx.save()
  ctx.scale(dpr / (highQuality ? 1 : 1), dpr / (highQuality ? 1 : 1)) // we already multiplied

  // Clear with a very slightly textured paper ground
  ctx.fillStyle = '#f1e9dc'
  ctx.fillRect(0, 0, W, H)

  // Subtle vertical grain (handwoven paper feel)
  ctx.strokeStyle = 'rgba(60,42,29,0.035)'
  ctx.lineWidth = 1
  for (let x = 8; x < W; x += 3.6) {
    ctx.beginPath()
    ctx.moveTo(x, 0)
    ctx.lineTo(x + (Math.sin(x * 0.03) * 1.2), H)
    ctx.stroke()
  }

  const marginX = 38
  const marginTop = 32
  const marginBottom = 34
  const weaveH = H - marginTop - marginBottom
  const weaveW = W - marginX * 2

  const numWarps = 11
  const warpSpacing = weaveW / (numWarps - 1)
  const warpColor = '#5c4636'

  // === WARP (the fixed vertical system tensions) ===
  ctx.strokeStyle = warpColor
  ctx.lineWidth = 1.6
  ctx.lineCap = 'round'

  for (let i = 0; i < numWarps; i++) {
    const x = marginX + i * warpSpacing
    // Very slight natural variation per warp
    const r = createSeededRandom(900 + i)
    ctx.beginPath()
    ctx.moveTo(x, marginTop - 6)
    // Draw with gentle curve so it doesn't feel machine perfect
    const midY = marginTop + weaveH * 0.5
    ctx.quadraticCurveTo(
      x + (r.next() - 0.5) * 1.6,
      midY,
      x + (r.next() - 0.5) * 0.8,
      marginTop + weaveH + 6
    )
    ctx.stroke()

    // Tiny "heddle" marks at top and bottom (the mechanical part of the loom)
    ctx.fillStyle = '#3f2a1d'
    ctx.fillRect(x - 2.5, marginTop - 11, 5, 4.5)
    ctx.fillRect(x - 2.5, marginTop + weaveH + 7, 5, 4.5)
  }

  // === WEFTS (the contributed status threat notes) ===
  const baseRowHeight = Math.max(5.5, Math.min(9.2, weaveH / Math.max(threads.length + 3, 9)))

  let y = marginTop + 14

  const animatingId = animateNew ? newThreadId : null
  const animProgress = animateNew ? (Date.now() % 1400) / 1400 : 1 // simple time based for live; real impl drives from state

  threads.forEach((thread, idx) => {
    const color = getThreadColor(thread)
    const tension = getWeftTension(thread)
    const r = createSeededRandom(1200 + idx * 17 + thread.emotionName.length)

    const isNew = thread.id === animatingId
    let drawProgress = 1
    if (isNew && animateNew) {
      // Shuttle flying in from left, then beating
      drawProgress = Math.min(1, Math.pow(animProgress * 1.15, 0.82))
    }

    const currentW = weaveW * drawProgress
    const rowJitter = (r.next() - 0.5) * 1.8
    const thisY = y + rowJitter

    ctx.strokeStyle = color
    ctx.fillStyle = color
    ctx.lineWidth = 1.35 + tension * 0.65
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'

    // Main weft path — slightly irregular like a hand thrown shuttle
    ctx.beginPath()
    ctx.moveTo(marginX - 3, thisY)

    const crossings = numWarps
    for (let c = 0; c < crossings; c++) {
      const targetX = marginX + (c * warpSpacing)
      if (targetX > marginX + currentW) break

      // Over/under tabby + natural hand variation
      const over = (idx + c) % 2 === 0
      const vy = over ? -1.6 : 1.9
      const vxJ = (r.next() - 0.5) * (1.6 * tension)
      const x = targetX + vxJ
      const yPos = thisY + vy + (r.next() - 0.5) * 0.9

      if (c === 0) ctx.lineTo(x, yPos)
      else ctx.quadraticCurveTo(x - warpSpacing * 0.35, thisY, x, yPos)
    }
    ctx.stroke()

    // Tiny "fuzz" fibers on high-recovery threads (visible texture)
    if (thread.recovery > 6.2) {
      ctx.lineWidth = 0.7
      ctx.strokeStyle = color + '55'
      for (let f = 0; f < 2; f++) {
        const fx = marginX + r.next() * Math.min(currentW, weaveW * 0.92)
        ctx.beginPath()
        ctx.moveTo(fx, thisY - 2.5)
        ctx.lineTo(fx + (r.next() - 0.5) * 7, thisY + 2.8)
        ctx.stroke()
      }
      ctx.strokeStyle = color
      ctx.lineWidth = 1.35 + tension * 0.65
    }

    // Beating pressure line (the reed pressing the weft home)
    // Only for threads that have "settled"
    if (!isNew || drawProgress > 0.96) {
      ctx.strokeStyle = 'rgba(60,42,29,0.18)'
      ctx.lineWidth = 0.65
      ctx.beginPath()
      ctx.moveTo(marginX - 1, thisY + 2.2)
      ctx.lineTo(marginX + Math.min(currentW, weaveW) + 1, thisY + 2.2)
      ctx.stroke()
      ctx.strokeStyle = color
    }

    y += baseRowHeight * (0.88 + (thread.frag - 5) * 0.015) // high frag threads take slightly more "space" in cloth
  })

  // Final "fell" line — the edge of the cloth (where the beater last pressed)
  const fellY = Math.min(y + 6, marginTop + weaveH - 4)
  ctx.strokeStyle = 'rgba(60,42,29,0.45)'
  ctx.lineWidth = 1.1
  ctx.beginPath()
  ctx.moveTo(marginX - 9, fellY)
  ctx.lineTo(marginX + weaveW + 9, fellY)
  ctx.stroke()

  // Subtle loom wood frame indication at edges
  ctx.strokeStyle = '#3f2a1d'
  ctx.lineWidth = 2.5
  ctx.strokeRect(9, 8, W - 18, H - 16)

  ctx.restore()
}

/**
 * High quality export. Returns a data URL (PNG) ready to be uploaded to R2
 * via the worker as the canonical public artifact for this cycle.
 */
export function exportLoomAsDataURL(threads: Thread[], title: string): string {
  const width = 1180
  const height = 620
  const canvas = document.createElement('canvas')
  canvas.width = width * 2
  canvas.height = height * 2

  const ctx = canvas.getContext('2d', { alpha: false })!
  // Background wash for print friendliness
  ctx.fillStyle = '#f8f4ed'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  drawLoom(ctx, {
    width: width * 2,
    height: height * 2,
    threads,
    highQuality: true,
  })

  // Add a very quiet footer label (only on exported artifact)
  ctx.fillStyle = 'rgba(60,42,29,0.35)'
  ctx.font = '500 15px Inter, system-ui, sans-serif'
  ctx.fillText(title.toUpperCase(), 48, canvas.height - 38)
  ctx.font = '13px Inter, system-ui, sans-serif'
  ctx.fillText('LOOM — first-generation status threads • ' + new Date().getFullYear(), 48, canvas.height - 18)

  return canvas.toDataURL('image/png')
}

/**
 * Create a smaller social/share card version (good for Twitter/LinkedIn previews)
 */
export function exportLoomCard(threads: Thread[], title: string): string {
  const width = 1200
  const height = 630
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d', { alpha: false })!

  // Rich paper + deep frame
  ctx.fillStyle = '#f4ede3'
  ctx.fillRect(0, 0, width, height)

  // Draw a centered, slightly smaller loom
  const loomW = 920
  const loomH = 420
  const offX = (width - loomW) / 2
  const offY = 78

  ctx.save()
  ctx.translate(offX, offY)
  drawLoom(ctx, {
    width: loomW,
    height: loomH,
    threads: threads.slice(-26), // last N for visual density on card
    highQuality: true,
  })
  ctx.restore()

  // Title block at top
  ctx.fillStyle = '#2f3459'
  ctx.font = '700 42px Georgia, serif'
  ctx.fillText(title, 92, 58)

  ctx.fillStyle = '#5c4636'
  ctx.font = '600 17px Inter, system-ui, sans-serif'
  const n = threads.length
  ctx.fillText(`${n} THREAD${n === 1 ? '' : 'S'}  •  A FIRST-GEN ROOMMATES WEAVE`, 94, 82)

  return canvas.toDataURL('image/png')
}
